import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { useScrollRestoration } from '@redpanda/utils';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import ManualPage from './pages/ManualPage';
import ProjectListPage from './pages/ProjectListPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import MyTasksPage from './pages/MyTasksPage';
import ReceivablesPage from './pages/ReceivablesPage';
import ProfitabilityPage from './pages/ProfitabilityPage';
import NotificationsPage from './pages/NotificationsPage';
import KanbanPage from './pages/KanbanPage';
import GanttPage from './pages/GanttPage';
import AdminPage from './pages/AdminPage';
import DataIOPage from './pages/DataIOPage';
import AcceptancePage from './pages/AcceptancePage';
import TimesheetPage from './pages/TimesheetPage';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage, MobileTopBar } from '@redpanda/ui';
import PrivateRoute from './components/PrivateRoute';
import Sidebar from './components/Sidebar';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { Role } from '@redpanda/types';

/** 捲動管理（共用 useScrollRestoration）：重新整理、上一頁、麵包屑回列表時還原捲動位置，進新頁面捲回頂端 */
const ScrollManager: React.FC = () => {
  useScrollRestoration(useLocation(), useNavigationType());
  return null;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const location = useLocation();
  const { t } = useTranslation();
  // 客戶驗收頁是給客戶看的，團隊成員自己打開預覽時也不顯示側欄
  const showChrome = isAuthenticated && !location.pathname.startsWith('/acceptance/');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // 設定動態頁面標題
  useEffect(() => {
    document.title = `RedPanda ${t('app.title')}`;
  }, [t]);

  // 記錄當前活動的系統頁面，供使用手冊自動捲到對應章節
  useEffect(() => {
    const systemPaths = ['/', '/projects', '/tasks', '/timesheets', '/receivables', '/profitability'];
    const currentPath = location.pathname;
    const activeSystemPath = systemPaths.find(path =>
      path === '/' ? currentPath === '/' : currentPath.startsWith(path)
    );
    if (activeSystemPath) {
      sessionStorage.setItem('lastActivePage', activeSystemPath);
    }
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          {/* 品牌 LOGO */}
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-2xl font-semibold text-gray-900 dark:text-white mb-6">RedPanda PM</div>

          {/* 載入動畫 */}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`App bg-white dark:bg-slate-950 min-h-screen flex transition-colors duration-300`}>
      <Toaster position="bottom-right" />
      <ScrollManager />

      {/* 左側導覽列 */}
      {showChrome && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 主要內容區域 */}
      <div className={`flex-1 min-w-0 flex flex-col ${showChrome ? 'md:ml-48' : ''}`}>
        {/* 手機版頂部列（R27）：取代原本浮動在左上角、會蓋住頁標題的選單按鈕 */}
        {showChrome && (
          <MobileTopBar
            title={`RedPanda ${t('app.title')}`}
            onOpenMenu={() => setIsMobileSidebarOpen(true)}
            menuLabel={t('common.navigation.openMenu')}
          />
        )}

        {/* 主要內容 */}
        <main className="flex-grow">
          <Routes>
            {/* 公開頁面 */}
            <Route path="/manual" element={<ManualPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/acceptance/:token" element={<AcceptancePage />} />

            {/* 需要認證的頁面 */}
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/projects" element={<ProjectListPage />} />
              <Route path="/projects/archived" element={<ProjectListPage isArchived={true} />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              <Route path="/kanban/:id" element={<KanbanPage />} />
              <Route path="/gantt/:id" element={<GanttPage />} />
              <Route path="/tasks" element={<MyTasksPage />} />
              <Route path="/receivables" element={<ReceivablesPage />} />
              <Route path="/timesheets" element={<TimesheetPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* 利潤報表：成本資料只有管理員看得到 */}
            <Route element={<PrivateRoute allowedRoles={[Role.ADMIN, Role.SUPER_ADMIN]} />}>
              <Route path="/profitability" element={<ProfitabilityPage />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN]} />}>
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/data-io" element={<DataIOPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ColorThemeProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;
